import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { ShoppingBag } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.1 } }),
};

export default function DigitalShop() {
  const { user, apiClient } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  const fetchProducts = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/api/products');
      setProducts(data);
    } catch { toast.error('Failed to load products'); }
    finally { setLoading(false); }
  }, [apiClient]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handlePurchase = async (productId) => {
    if (!user) { toast.error('Please login to purchase'); return; }
    try {
      const originUrl = window.location.origin;
      const { data } = await apiClient.post('/api/payments/create-checkout', null, {
        params: { origin_url: originUrl, item_type: 'product', item_id: productId },
      });
      window.location.href = data.url;
    } catch { toast.error('Failed to initiate payment.'); }
  };

  const categories = ['all', ...new Set(products.map((p) => p.category))];
  const filteredProducts = selectedCategory === 'all' ? products : products.filter((p) => p.category === selectedCategory);

  return (
    <div className="min-h-screen pt-16 bg-[var(--bg-alt)]" data-testid="digital-shop-page">
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" animate="visible" className="text-center mb-12">
            <p className="uppercase tracking-[0.25em] text-xs font-bold text-[var(--primary-blue)] mb-3">Digital Resources</p>
            <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4">Digital Shop</h1>
            <p className="text-base md:text-lg text-[var(--text-body)] max-w-2xl mx-auto">
              Premium toolkits, workbooks, and templates for parents, clinicians, and educators.
            </p>
          </motion.div>

          {/* Category Filter */}
          {categories.length > 1 && (
            <div className="flex gap-3 mb-10 justify-center flex-wrap" data-testid="category-filter">
              {categories.map((cat) => (
                <button key={cat} onClick={() => setSelectedCategory(cat)}
                  className={`px-5 py-2 rounded-xl font-semibold text-sm transition-all ${
                    selectedCategory === cat
                      ? 'bg-[var(--primary-blue)] text-white shadow-lg shadow-blue-500/20'
                      : 'bg-white text-[var(--text-body)] border border-[var(--border-color)] hover:border-[var(--primary-blue)]'
                  }`}
                  data-testid={`category-${cat}-btn`}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--primary-blue)]" />
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-16 card-elevated">
              <ShoppingBag size={32} className="text-[var(--border-color)] mx-auto mb-3" />
              <p className="text-[var(--text-body)]">No products in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product, i) => (
                <motion.div key={product.id} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i}
                  className="bg-white/70 backdrop-blur-xl border border-white/40 shadow-[0_8px_32px_rgba(15,37,55,0.06)] rounded-2xl overflow-hidden hover:shadow-[0_16px_48px_rgba(15,37,55,0.1)] hover:-translate-y-1 transition-all duration-300"
                  data-testid={`product-${product.id}`}>
                  <div className="h-48 bg-gradient-to-br from-blue-50 to-lime-50 flex items-center justify-center">
                    <ShoppingBag size={48} className="text-[var(--primary-blue)]/20" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-lg font-heading font-bold flex-1">{product.title}</h3>
                      <span className="px-2.5 py-1 bg-[var(--primary-green)]/20 text-lime-700 text-xs font-bold rounded-lg ml-2">
                        {product.category}
                      </span>
                    </div>
                    <p className="text-sm text-[var(--text-body)] mb-6">{product.description}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-[var(--border-color)]">
                      <p className="text-2xl font-heading font-bold text-[var(--primary-blue)]">${product.price}</p>
                      <button onClick={() => handlePurchase(product.id)} className="btn-primary text-sm !px-5 !py-2" data-testid={`purchase-${product.id}-btn`}>
                        Purchase
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
